import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { LanguageService, PosthogAnalyticsService } from '@mzic/mzic-services';
import { AccordionComponent } from '../../components/accordion/accordion.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { HeaderComponent } from '../../components/header/header.component';

@Component({
  selector: 'mzic-market',
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AccordionComponent,
    TranslocoModule,
    RouterModule,
  ],
  templateUrl: './market.component.html',
  styleUrl: './market.component.scss',
  providers: [
    provideTranslocoScope({
      scope: 'market',
      alias: 'marketLang',
    }),
  ],
})
export class MarketComponent implements OnInit, AfterViewInit {
  languageService = inject(LanguageService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private analyticsService = inject(PosthogAnalyticsService);

  activeAccordion: string | null = null;
  showRightButton = true;
  showLeftButton = false;

  // URLs das imagens do gráfico "industry size" baseadas na linguagem
  industrySizeChartDesktop = computed(() => {
    const lang = this.languageService.languageState();
    return `https://mzic-website.s3.us-east-1.amazonaws.com/${lang}-music-industry-size-chart-desktop.svg`;
  });

  industrySizeChartMobile = computed(() => {
    const lang = this.languageService.languageState();
    return `https://mzic-website.s3.us-east-1.amazonaws.com/${lang}-music-industry-size-chart-mobile.svg`;
  });

  // URL da imagem do gráfico "music royalties flow" baseada na linguagem
  musicRoyaltiesFlowChart = computed(() => {
    const lang = this.languageService.languageState();
    return `https://mzic-website.s3.us-east-1.amazonaws.com/${lang}-music-royalties-flow-chart.svg`;
  });

  // URLs das imagens do gráfico "recorded industry" baseadas na linguagem
  recordedIndustryChartDesktop = computed(() => {
    const lang = this.languageService.languageState();
    return `https://mzic-website.s3.us-east-1.amazonaws.com/${lang}-recorded-industry-chart-desktop.svg`;
  });

  recordedIndustryChartMobile = computed(() => {
    const lang = this.languageService.languageState();
    return `https://mzic-website.s3.us-east-1.amazonaws.com/${lang}-recorded-industry-chart-mobile.svg`;
  });

  // URL da imagem "top countries list" baseada na linguagem
  topCountriesList = computed(() => {
    const lang = this.languageService.languageState();
    return `https://mzic-website.s3.us-east-1.amazonaws.com/${lang}-top-countries-list.svg`;
  });

  // URLs das imagens do segundo gráfico "recorded industry" no side-by-side
  recordedIndustryChartDesktopSideBySide = computed(() => {
    const lang = this.languageService.languageState();
    return `https://mzic-website.s3.us-east-1.amazonaws.com/${lang}-streaming-trends-chart-desktop.svg`;
  });

  recordedIndustryChartMobileSideBySide = computed(() => {
    const lang = this.languageService.languageState();
    return `https://mzic-website.s3.us-east-1.amazonaws.com/${lang}-streaming-trends-chart-mobile.svg`;
  });

  @ViewChild('carouselContainer') carouselContainer!: ElementRef;
  @ViewChild('industrySizeAccordion', { read: ElementRef })
  industrySizeAccordion!: ElementRef;
  @ViewChild('howMusicGeneratesRevenueAccordion', { read: ElementRef })
  howMusicGeneratesRevenueAccordion!: ElementRef;
  @ViewChild('whyInvestInMusicAccordion', { read: ElementRef })
  whyInvestInMusicAccordion!: ElementRef;
  @ViewChild('moneyFlowAccordion', { read: ElementRef })
  moneyFlowAccordion!: ElementRef;
  @ViewChild('musicEvolutionAccordion', { read: ElementRef })
  musicEvolutionAccordion!: ElementRef;

  revenueCards = [
    {
      type: 'badges',
      key: 'streamingDigital',
      badges: [
        {
          icon: 'assets/icons/mzic-spotify-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.spotify',
        },
        {
          icon: 'https://mzic-website.s3.us-east-1.amazonaws.com/youtube-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.youtubeMusic',
        },
        {
          icon: 'assets/icons/mzic-apple-music-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.appleMusic',
        },
        {
          icon: 'https://mzic-website.s3.us-east-1.amazonaws.com/amazon-music-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.amazonMusic',
        },
        {
          icon: 'https://mzic-website.s3.us-east-1.amazonaws.com/instagram-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.instagram',
        },
        {
          icon: 'https://mzic-website.s3.us-east-1.amazonaws.com/tik-tok-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.tiktok',
        },
      ],
    },
    {
      type: 'icons',
      key: 'publicExecution',
      icons: [
        'https://mzic-website.s3.us-east-1.amazonaws.com/radio-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/restaurant-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/gym-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/bag-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/club-icon.svg',
      ],
    },
    {
      type: 'icons',
      key: 'liveMusic',
      icons: [
        'https://mzic-website.s3.us-east-1.amazonaws.com/microphone-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/transmission-icon.svg',
      ],
    },
    {
      type: 'icons',
      key: 'synchronization',
      icons: [
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-cut-scene-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-controller-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-tv-icon.svg',
      ],
    },
  ];

  constructor() {
    effect(() => {
      const lang = this.languageService.languageState();
      const current = this.router.url.split('/').pop();

      if (lang !== '') {
        this.router.navigate([`${lang}/${current}`]);
      }
    });
  }

  ngOnInit() {
    this.setLanguageFromRoute();

    // Track page view with PostHog
    this.analyticsService.trackPageView('market', {
      language: this.languageService.languageState(),
    });
  }

  ngAfterViewInit() {
    this.checkScrollPosition();
  }

  setLanguageFromRoute() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const lang = params['lang'];
        if (lang) {
          this.translocoService.setActiveLang(lang);
          this.languageService.languageState.set(lang);
        }
      });
  }

  toggleAccordion(id: string) {
    const wasPreviouslyOpen = this.activeAccordion === id;
    const action = wasPreviouslyOpen ? 'close' : 'open';
    const timeOnSection = this.analyticsService.getTimeOnSection(
      `accordion_${id}`,
    );

    // Map accordion IDs to event names
    const eventNameMap: Record<string, string> = {
      industrySize: 'market_accordion_industry_size',
      howMusicGeneratesRevenue: 'market_accordion_revenue_generation',
      whyInvestInMusic: 'market_accordion_investment_reasons',
    };

    // Track accordion interaction
    this.analyticsService.captureWithContext(
      eventNameMap[id] || 'market_accordion_unknown',
      {
        accordion_id: id,
        action: action,
        time_on_section: timeOnSection,
        was_previously_open: wasPreviouslyOpen,
        section: 'accordions',
        language: this.languageService.languageState(),
      },
    );

    // Se está clicando no mesmo accordion, apenas fecha
    if (this.activeAccordion === id) {
      this.activeAccordion = null;
      this.analyticsService.stopSectionTimer(`accordion_${id}`);
      return;
    }

    // Stop timer for previous accordion if exists
    const previousAccordionId = this.activeAccordion;
    if (this.activeAccordion) {
      this.analyticsService.stopSectionTimer(
        `accordion_${this.activeAccordion}`,
      );
    }

    // Se havia um accordion aberto, calcula a posição futura e scrolla imediatamente
    if (previousAccordionId) {
      this.openAccordionWithSmartScroll(id, previousAccordionId);
    } else {
      // Se nenhum estava aberto, abre direto
      this.openAccordionWithScroll(id);
    }
  }

  openAccordionWithSmartScroll(newId: string, previousId: string) {
    const newAccordionElement = this.getAccordionElement(newId);
    const previousAccordionElement = this.getAccordionElement(previousId);

    if (!newAccordionElement) {
      this.activeAccordion = newId;
      this.analyticsService.startSectionTimer(`accordion_${newId}`);
      return;
    }

    const newElement = newAccordionElement.nativeElement;
    const offset = 120;

    // Detecta se é mobile ou desktop
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      // Mobile: Comportamento rápido e responsivo
      this.activeAccordion = null;

      requestAnimationFrame(() => {
        this.activeAccordion = newId;
        this.analyticsService.startSectionTimer(`accordion_${newId}`);

        requestAnimationFrame(() => {
          const finalRect = newElement.getBoundingClientRect();
          const targetScrollPosition =
            finalRect.top + window.pageYOffset - offset;

          window.scrollTo({
            top: targetScrollPosition,
            behavior: 'smooth',
          });

          setTimeout(() => this.checkScrollPosition(), 100);
        });
      });
    } else {
      // Desktop: Comportamento sequencial e suave para evitar chicote
      this.activeAccordion = null;

      setTimeout(() => {
        this.activeAccordion = newId;
        this.analyticsService.startSectionTimer(`accordion_${newId}`);

        setTimeout(() => {
          const finalRect = newElement.getBoundingClientRect();
          const targetScrollPosition =
            finalRect.top + window.pageYOffset - offset;

          window.scrollTo({
            top: targetScrollPosition,
            behavior: 'smooth',
          });

          setTimeout(() => this.checkScrollPosition(), 100);
        }, 150);
      }, 300);
    }
  }

  openAccordionWithScroll(id: string) {
    const accordionElement = this.getAccordionElement(id);

    if (!accordionElement) {
      this.activeAccordion = id;
      this.analyticsService.startSectionTimer(`accordion_${id}`);
      return;
    }

    // Abre o accordion imediatamente
    this.activeAccordion = id;
    this.analyticsService.startSectionTimer(`accordion_${id}`);

    // Aguarda um frame para o DOM atualizar com o novo estado
    requestAnimationFrame(() => {
      const element = accordionElement.nativeElement;
      const rect = element.getBoundingClientRect();
      const offset = 120;

      // Verifica se o título do accordion está visível
      const isVisible = rect.top >= offset && rect.top <= window.innerHeight;

      if (!isVisible) {
        // Se não está visível, scrolla para posicionar o accordion no topo
        const elementPosition = rect.top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }

      // Atualiza a posição do carousel se necessário
      setTimeout(() => this.checkScrollPosition(), 100);
    });
  }

  getAccordionElement(id: string): ElementRef | undefined {
    switch (id) {
      case 'industrySize':
        return this.industrySizeAccordion;
      case 'howMusicGeneratesRevenue':
        return this.howMusicGeneratesRevenueAccordion;
      case 'whyInvestInMusic':
        return this.whyInvestInMusicAccordion;
      case 'moneyFlow':
        return this.moneyFlowAccordion;
      case 'musicEvolution':
        return this.musicEvolutionAccordion;
      default:
        return undefined;
    }
  }

  checkScrollPosition() {
    if (this.carouselContainer) {
      const container = this.carouselContainer.nativeElement;
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      // No início
      this.showLeftButton = scrollLeft > 10;

      // No final (com margem de 10px de tolerância)
      this.showRightButton = scrollLeft < scrollWidth - clientWidth - 10;
    }
  }

  scrollCarousel() {
    if (this.carouselContainer) {
      const container = this.carouselContainer.nativeElement;
      const card = container.querySelector('.revenue-card') as HTMLElement;

      if (card) {
        const cardWidth = card.offsetWidth;
        const gap = window.innerWidth <= 768 ? 16 : 26;
        const scrollAmount = cardWidth + gap;
        const scrollPosition = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        const cardsVisible = Math.ceil(clientWidth / (cardWidth + gap));

        // Track carousel scroll
        this.analyticsService.captureWithContext('market_carousel_scroll', {
          direction: 'forward',
          scroll_position: scrollPosition,
          cards_visible: cardsVisible,
          section: 'revenue_cards',
          language: this.languageService.languageState(),
        });

        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }

      setTimeout(() => this.checkScrollPosition(), 400);
    }
  }

  scrollCarouselBack() {
    if (this.carouselContainer) {
      const container = this.carouselContainer.nativeElement;
      const card = container.querySelector('.revenue-card') as HTMLElement;

      if (card) {
        const cardWidth = card.offsetWidth;
        const gap = window.innerWidth <= 768 ? 16 : 26;
        const scrollAmount = cardWidth + gap;
        const scrollPosition = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        const cardsVisible = Math.ceil(clientWidth / (cardWidth + gap));

        // Track carousel scroll
        this.analyticsService.captureWithContext('market_carousel_scroll', {
          direction: 'backward',
          scroll_position: scrollPosition,
          cards_visible: cardsVisible,
          section: 'revenue_cards',
          language: this.languageService.languageState(),
        });

        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth',
        });
      }

      setTimeout(() => this.checkScrollPosition(), 400);
    }
  }
}
